import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, Lock, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/tyretrack`;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error("Inserisci email e password");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      if (response.status === 200) {
        const data = await response.json();
        
        // Save user data
        login({
          email: formData.email,
          displayName: data.DisplayName || data.displayName || formData.email,
          ...data
        });

        toast.success(`Benvenuto ${data.DisplayName || data.displayName || ""}!`);
        navigate("/");
      } else {
        toast.error("Login o password errata");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Errore di connessione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      data-testid="login-page" 
      className="min-h-screen flex items-center justify-center p-4 bg-background"
    >
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-primary">Tyre</span>Track Pro
            </h1>
          </div>
          <CardTitle className="text-xl">Accedi</CardTitle>
          <CardDescription>Inserisci le tue credenziali per accedere</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="login-email"
                  placeholder="nome@esempio.it"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 h-12 bg-input/50 border-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="login-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 h-12 bg-input/50 border-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Accesso in corso...
                </>
              ) : (
                <>
                  <LogIn size={18} className="mr-2" />
                  Login
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <Link
              to="/register"
              data-testid="register-link"
              className="text-sm text-primary hover:underline block"
            >
              Registrati
            </Link>
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-sm text-muted-foreground hover:text-primary hover:underline block"
            >
              Password dimenticata?
            </Link>
            <p className="text-xs text-muted-foreground text-center mt-2">
              ©{new Date().getFullYear()} - TyreTrack - v{process.env.REACT_APP_VERSION}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
